import { ChevronRight, Container, Lock, MapPin, PackageCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

import { CONTAINER_LINE_STATUS_STYLES } from "../../constants/containerOrderConstants";
import type { ContainerOrderLine } from "../../types/containerOrders";
import { activeStopsOf, completedStepCount } from "../../utils/containerLineSteps";
import { blockingRequirements } from "../../utils/containerLineStart";
import { nextDueAt } from "../../utils/containerLineQueue";

/**
 * What the driver is doing after this container.
 *
 * Sits under the route timeline, which is the right place for it: the timeline
 * answers "how much of this box is left" and this answers the question that
 * comes immediately after. Both are reference — neither records anything — so
 * both live below the frame where the actual work happens.
 *
 * Always rendered, including when there is nothing next. A card that vanishes
 * on an empty queue is indistinguishable from a card that failed to load, and a
 * driver deciding whether to head home wants to be *told* there is nothing
 * else, not left to infer it from an absence.
 *
 * Every field on it is optional except the container's own number and the route
 * — the same rule the stop frame follows, and for the same reason: blanks are
 * dropped rather than drawn as dashes, because a row of em-dashes is a screen
 * apologising for itself. What is guaranteed is guaranteed by the model:
 * `lineNumber` is derived from the order number and the sequence, both written
 * when the container is generated.
 */

/** `12 Mar · 09:00`, or nothing at all when nobody has scheduled it. */
const dueLabel = (line: ContainerOrderLine): string => {
  const at = nextDueAt(line);
  if (at === null) return "";
  return new Date(at).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface DriverNextContainerProps {
  next: ContainerOrderLine | null;
  onOpen: (line: ContainerOrderLine) => void;
  /**
   * Drawn inside the completion sheet rather than on the page.
   *
   * Same card, louder: on the sheet it is the thing being offered rather than a
   * note at the bottom of a screen, so it loses the section heading the page
   * version needs and gains the emphasis the moment deserves.
   */
  variant?: "card" | "sheet";
}

export function DriverNextContainer({
  next,
  onOpen,
  variant = "card",
}: DriverNextContainerProps) {
  const { t } = useTranslation("containerOrders");

  if (!next) {
    return (
      <div
        className={
          variant === "sheet"
            ? "rounded-[10px] bg-[#F7F6F2] px-3 py-3 text-center"
            : "rounded-[14px] border border-[#E4E2DA] bg-white px-3 py-3 text-center"
        }
      >
        <p className="flex items-center justify-center gap-1.5 text-[13px] text-[#6B6A64]">
          <PackageCheck className="h-4 w-4 shrink-0 text-[#A6A49C]" />
          {t("driver.queueEmpty")}
        </p>
      </div>
    );
  }

  /**
   * What the office still owes before this container can be reported in at.
   *
   * Shown rather than used to hide the card. A driver sent to a box that cannot
   * legally start needs to know *now*, from the cab, so they can ring dispatch
   * before driving to a terminal — hiding it would turn a phone call into a
   * wasted leg.
   *
   * The arrival gate alone, because this card is about a container the driver
   * has not reached. It used to list both gates, which put the container
   * number, the tare and the max payload on a card offering a box the driver
   * cannot see yet — three figures that are read off the door on arrival and
   * are supposed to be missing at this point. The departure gate is raised on
   * the execution screen, at the stop, where it can actually be answered.
   */
  const missing = blockingRequirements(next, completedStepCount(next));

  const route = activeStopsOf(next)
    .map((stop) => stop.companyName?.trim() || stop.label?.trim() || "—")
    .join(" → ");

  const due = dueLabel(next);

  return (
    <div
      className={
        variant === "sheet"
          ? ""
          : "overflow-hidden rounded-[14px] border border-[#E4E2DA] bg-white"
      }
    >
      {variant === "card" && (
        <p className="px-3 pt-2.5 text-[13px] font-semibold text-[#1A1A18]">
          {t("driver.queueTitle")}
        </p>
      )}

      <button
        type="button"
        onClick={() => onOpen(next)}
        className={`w-full px-3 py-2.5 text-left ${
          variant === "sheet"
            ? "rounded-[10px] border border-[#EF9F27] bg-[#FAEEDA]"
            : ""
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <Container className="h-4 w-4 shrink-0 text-[#854F0B]" />
            <span className="truncate text-[13px] font-semibold text-[#1A1A18]">
              {next.lineNumber}
            </span>
            {/* The box's own number when it has one. A container nobody has
                collected yet has not got one, and that is not a gap — it is
                what the driver is going to read off the door. */}
            {next.containerNumber && (
              <span className="truncate font-mono text-[11.5px] text-[#A6A49C]">
                {next.containerNumber}
              </span>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <span
              className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10.5px] font-medium ${
                CONTAINER_LINE_STATUS_STYLES[next.status] ??
                CONTAINER_LINE_STATUS_STYLES.planned
              }`}
            >
              {t(`linesBlock.statuses.${next.status}`)}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-[#C9C7BE]" />
          </div>
        </div>

        {/* The run as this driver will actually make it — read off the route
            rather than the three anchors, so a terminal that is not being used
            is not shown as somewhere they are expected to go. */}
        <p className="mt-1 flex items-center gap-1.5 text-[12px] text-[#6B6A64]">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-[#A6A49C]" />
          <span className="truncate">{route}</span>
        </p>

        {/* Only when somebody has scheduled it. An unscheduled container still
            belongs in the queue — it is simply one nobody has booked a slot for
            yet, and inventing "today" for it would be inventing a commitment. */}
        {due && (
          <p className="mt-0.5 text-[11.5px] text-[#854F0B]">
            {t("driver.queueDue", { when: due })}
          </p>
        )}

        {missing.length > 0 && (
          <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-[#FCEEE2] px-2 py-1.5 text-[11px] text-[#8A5A28]">
            <Lock className="mt-0.5 h-3 w-3 shrink-0" />
            <span>
              {t("driver.blocked.short", {
                fields: missing
                  .map((key) => t(`driver.requirements.${key}`))
                  .join(", "),
              })}
            </span>
          </p>
        )}
      </button>
    </div>
  );
}

export default DriverNextContainer;
